import { ACCOUNT_FEATURE, APP_FEATURE, isBrowserCompatible } from '@socialive/common';
import { useFeature } from '@socialive/features';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getStudioURL, normalizeStudioURL } from '~/helpers/broadcastHelpers';
import { selectComposerEnvUrls, selectCurrentUserId } from '~/store';
import fetchComposerTokens from '~/store/env/thunks/fetchComposerTokens';

const joinBroadcast = async (hash, version, studioLink, composerTokens) => {
  const windowType = sessionStorage.getItem('studioWindowType');

  const windowParams =
    windowType !== 'tab'
      ? `width=${window.screen.availWidth},height=${window.screen.availHeight},titlebar=no,menubar=no,status=no`
      : '';

  const studioUrl = await normalizeStudioURL(studioLink, hash, version, composerTokens);

  window.webcasterWindow = window.open(studioUrl, 'webcaster', windowParams);
};

const pageLoadTime = new Date();

export default ({ hash, host, trackEvent }) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector(selectCurrentUserId);
  const composerEnvUrls = useSelector(selectComposerEnvUrls);

  const [isIncompatibleBrowser, setIsIncompatibleBrowser] = useState(false);
  const [, { versions }] = useFeature(APP_FEATURE.APP_CONFIG);
  const [, { cutoverDate, cutoverMessage }] = useFeature(ACCOUNT_FEATURE.SINGLE_DOMAIN);

  const handleJoinBroadcast = useCallback(async () => {
    if (navigator.userAgent.match('Mobile')) {
      document.location = 'https://socialive.app.link/join';
      return;
    }

    const cutoverTime = cutoverDate && new Date(cutoverDate);
    const currentTime = new Date();
    if (cutoverTime && currentTime > cutoverTime && cutoverTime > pageLoadTime) {
      // eslint-disable-next-line no-alert
      alert(cutoverMessage);
      window.location.reload();
    }

    if (!isBrowserCompatible()) {
      setIsIncompatibleBrowser(true);
      return;
    }
    const { id } = host;
    const { studio, guest } = versions;

    const isHost = currentUserId === id;
    const version = (isHost ? studio : guest) ?? Date.now();

    const studioURLOverride = sessionStorage.getItem('studioURLOverride');
    const studioLink = await getStudioURL(studioURLOverride, isHost);

    let composerTokens;
    if (composerEnvUrls.includes(studioLink)) {
      composerTokens = await dispatch(fetchComposerTokens({ hash })).unwrap();
    }

    await joinBroadcast(hash, version, studioLink, composerTokens);

    const broadcastRole = isHost ? 'host' : 'guest';
    trackEvent(`Broadcast Joined as ${broadcastRole}`, { broadcast_hash: hash, role: broadcastRole });
  }, [cutoverDate, cutoverMessage, host, versions, currentUserId, composerEnvUrls, hash, trackEvent, dispatch]);

  const toggleIncompatibleBrowser = useCallback(() => setIsIncompatibleBrowser((value) => !value), []);

  return [handleJoinBroadcast, isIncompatibleBrowser, toggleIncompatibleBrowser];
};
